// Pure Task-title search rules for the shell palette. The palette reads the already-authorised Task-board
// projection, then this module owns every client-side query/ranking decision: bounded input, exact-title
// precedence, useful contains matches, and the result cap. It never opens chat or performs a network call.

import type { BoardTaskRow } from '../surfaces/projections.ts';

export const TASK_SEARCH_QUERY_LIMIT = 120;
export const TASK_SEARCH_RESULT_LIMIT = 20;

const MIN_CONTAINS_QUERY_LENGTH = 3;

/** Keep browser-held search input small and predictable before any matching work runs. */
export function boundTaskSearchQuery(value: string): string {
  return value.slice(0, TASK_SEARCH_QUERY_LIMIT);
}

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase('en-GB');
}

interface RankedTask {
  readonly task: BoardTaskRow;
  readonly rank: number;
  readonly sourceIndex: number;
}

/**
 * Rank title matches without inventing a second Task index:
 * exact title (including every duplicate) -> title prefix -> title contains.
 * Very short input only returns an exact title, avoiding a noisy one/two-character contains wall.
 */
export function searchTaskTitles(
  tasks: readonly BoardTaskRow[],
  rawQuery: string,
): readonly BoardTaskRow[] {
  const query = normalized(boundTaskSearchQuery(rawQuery));
  if (query.length === 0) return [];

  const ranked: RankedTask[] = [];
  tasks.forEach((task, sourceIndex) => {
    const title = normalized(task.title);
    if (title === query) {
      ranked.push({ task, rank: 0, sourceIndex });
      return;
    }
    if (query.length < MIN_CONTAINS_QUERY_LENGTH) return;
    if (title.startsWith(query)) {
      ranked.push({ task, rank: 1, sourceIndex });
      return;
    }
    if (title.includes(query)) ranked.push({ task, rank: 2, sourceIndex });
  });

  ranked.sort((left, right) => left.rank - right.rank || left.sourceIndex - right.sourceIndex);
  return ranked.slice(0, TASK_SEARCH_RESULT_LIMIT).map(({ task }) => task);
}
